import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../src/app/store';
import { axiosApiCall } from '../../services/apiService';
import { Address, AnalyticsData, ApiResponse, AxiosData, IUserDetails, PartyAddress, UltimateParent, UserDetails } from '../../utils/types';
import { Feature } from '../../utils/featureFlag';

export interface FeatureFlags {
  [key: string]: boolean;
}

export interface HomeState {
  userDetails: IUserDetails;
  isLoggedOut: boolean;
  navigationFromDashboard: boolean;
  analytics: AnalyticsData;
  axiosData: AxiosData;
  featureFlags: FeatureFlags;
}

const initialState: HomeState = {
  userDetails: {
    address: []
  },
  isLoggedOut: false,
  navigationFromDashboard: false,
  analytics: {
    accountOpenTimestamp: '',
    draftRandomId: ''
  },
  axiosData: {
    showError: false,
    showLoader: false
  },
  featureFlags: {}
};

export const fetchUserDetails = createAsyncThunk('asyncFetchUserDetails', async (_, thunkAPI) => {
  const apiUrl: string = process.env.UXP_ACCESS_MANAGEMENT as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/user/details`);
  let userObject: IUserDetails = {};

  if (responseData?.data?.resultSet) {
    const { personaId, sponsorCode, firstName, middleName, lastName, businessEmailId, appConfigItems = {} } = responseData.data.resultSet as UserDetails;
    userObject = {
      personaId: personaId,
      sponsorCode: sponsorCode,
    };
    if (responseData?.meta?.records > 0) {
      userObject = {
        ...userObject,
        name: `${firstName}${middleName ? ' ' + middleName : ''} ${lastName}`,
        businessEmailId: businessEmailId
      };
    } else {
      const tokenInfo = JSON.parse(localStorage.getItem('okta-token-storage') as string);
      userObject = {
        ...userObject,
        name: tokenInfo?.idToken?.claims?.name,
        businessEmailId: tokenInfo?.idToken?.claims?.email
      };
    }
    thunkAPI.dispatch(setFeatureFlags(appConfigItems));
  }
  return userObject;
});

export const fetchUltimateParentData = createAsyncThunk('asyncFetchUltimateParentData', async (params: string) => {
  const apiUrl: string = process.env.UXP_SEARCH_AND_PREFILL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/ultimate-parent?sponsorCode=${params}`);
  return responseData.data.resultSet as UltimateParent[];
});

export const fetchReferenceAddressData = createAsyncThunk('asyncFetchReferenceAddressData', async (params: string) => {
  const apiUrl: string = process.env.UXP_SEARCH_AND_PREFILL as string;
  const responseData: ApiResponse = await axiosApiCall('GET', `${apiUrl}/address?sponsorCode=${params}`);
  return responseData.data.resultSet as Address[];
});

export const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    resetUserDetails: () => initialState,
    setAxiosData: (state, action: PayloadAction<AxiosData>) => {
      state.axiosData = { ...state.axiosData, ...action.payload };
    },
    setUserLoggedOut: (state, action: PayloadAction<boolean>) => {
      state.isLoggedOut = action.payload;
    },
    setUserDetails: (state, action: PayloadAction<IUserDetails>) => {
      state.userDetails = action.payload;
    },
    setFeatureFlags: (state, action: PayloadAction<{ [key: string]: string }>) => {
      const { editFeature, taxExemptFeature, sorryPage } = action.payload;
      // Map feature flags from API to Feature name enum in featureFlag utils
      state.featureFlags = {
        ...state.featureFlags,
        [Feature.EDIT]: editFeature?.toLowerCase() === 'true',
        [Feature.TAX_EXEMPT]: taxExemptFeature?.toLowerCase() === 'true',
        [Feature.MAINTENANCE]: sorryPage?.toLowerCase() === 'true'
      };
    },
    updatePrefillAddress: (state, action: PayloadAction<Address>) => {
      state.userDetails.address = [...(state.userDetails.address || []), action.payload];
    },
    setUserAddressDetails: (state, action: PayloadAction<PartyAddress[]>) => {
      const addressList = state.userDetails.address as Address[];
      action.payload.forEach((addressObject: PartyAddress) => {
        const index = addressList?.findIndex((item: Address) => item.id === addressObject.addressId);
        if (index === -1) addressList.push(
          {
            id: addressObject?.addressId,
            line1: addressObject?.addressLineOne,
            line2: addressObject?.addressLineTwo,
            city: addressObject?.city,
            state: addressObject?.state,
            country: addressObject?.country,
            postalCode: addressObject?.postalCode,
            addressPurpose: addressObject?.addressPurpose,
            addressType: addressObject?.addressType,
            type: addressObject?.type
          } as Address
        );
      });
      state.userDetails.address = [...addressList];
    },
    setNavigationFromDashboard: (state, action: PayloadAction<boolean>) => {
      state.navigationFromDashboard = action.payload;
    },
    setAnalyticsData: (state, action: PayloadAction<AnalyticsData>) => {
      state.analytics = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      state.userDetails = { ...action.payload, address: [] };
    });
    builder.addCase(fetchUltimateParentData.fulfilled, (state, action) => {
      state.userDetails = { ...state.userDetails, ultimateParent: { ...action.payload[0] } };
    });
    builder.addCase(fetchReferenceAddressData.fulfilled, (state, action) => {
      state.userDetails.address = [...action.payload];
    });
  },
});

export const { resetUserDetails, setAxiosData, setUserLoggedOut, setUserDetails, updatePrefillAddress, setUserAddressDetails, setNavigationFromDashboard, setAnalyticsData, setFeatureFlags } = homeSlice.actions;

export const selectUserDetails = (state: RootState) => state.home.userDetails;
export const selectUserLoggedOut = (state: RootState) => state.home.isLoggedOut;
export const selectNavigationFromDashboard = (state: RootState) => state.home.navigationFromDashboard;
export const selectAnalyticsData = (state: RootState) => state.home.analytics;
export const selectAxiosData = (state: RootState) => state.home.axiosData;
export const selectFeatureFlags = (state: RootState) => state.home.featureFlags;

export default homeSlice.reducer;
